import { App } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fundsLine, kbKeys, useFunds } from '@entities/knowledge';
import { sourceApi, type ScrapeEstimateResult } from '@entities/source';
import { watchKb } from '@features/kb-watch';
import { extractErrorMessage } from '@shared/api';
import { IconCheck, IconGlobe, num } from '@shared/ui/v2';
import { money } from './kbUi';
import './knowledge.css';
import './site.css';

const REASONS: Record<string, { title: string; what: string }> = {
  no_sitemap: {
    title: 'У сайта нет карты сайта (sitemap.xml)',
    what: 'Попросите разработчика сайта включить карту сайта — или загрузите нужные страницы файлами (сохраните их как PDF).',
  },
  cloudflare_blocked: {
    title: 'Сайт закрыт для роботов',
    what: 'Защита сайта не пускает нашего робота. Попросите разработчика разрешить доступ или загрузите страницы файлами.',
  },
  invalid_url: {
    title: 'Адрес не открывается',
    what: 'Проверьте адрес: он должен начинаться с https:// и открываться в браузере.',
  },
  timeout: {
    title: 'Сайт слишком долго не отвечает',
    what: 'Попробуйте проверить ещё раз через несколько минут.',
  },
  internal_error: {
    title: 'Не получилось проверить сайт',
    what: 'Попробуйте ещё раз. Если повторится — напишите нам.',
  },
};

/** Экран 5б «Подключить сайт» — отдельная страница (утверждён 28.10.2026). Только текст страниц. */
export function SiteConnectPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const sourceId = sp.get('source');
  const [url, setUrl] = useState(sp.get('url') ?? 'https://');
  const [starting, setStarting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { data: funds } = useFunds();

  const { data: est } = useQuery<ScrapeEstimateResult | null>({
    queryKey: ['scrape-estimate', sourceId],
    queryFn: async () => {
      try {
        return await sourceApi.getScrapeEstimate(sourceId!);
      } catch (e) {
        const st = (e as { response?: { status?: number } }).response?.status;
        if (st === 404 || st === 410) return null;
        throw e;
      }
    },
    enabled: !!sourceId,
    refetchInterval: (q) => (q.state.data?.status === 'in_progress' ? 3000 : false),
  });

  const check = async () => {
    const u = url.trim();
    if (!/^https?:\/\/[^\s.]+\.[^\s]+/.test(u)) {
      message.info('Впишите адрес сайта полностью, например https://richat.ru');
      return;
    }
    setStarting(true);
    try {
      const r = await sourceApi.startScrapeEstimate({
        assistant_id: id,
        url: u,
        process_images: false,
      });
      setSp({ source: r.source_id, url: u }, { replace: true });
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setStarting(false);
    }
  };

  const ready = est && est.status === 'ready' ? est : null;
  const failed = est && est.status === 'failed' ? est : null;
  const scanning = (!!sourceId && est === undefined) || est?.status === 'in_progress';
  const pages = ready?.pages_to_process ?? 0;
  const total = pages * 0.5;
  const fl = fundsLine(funds, total);
  const short = !!ready && fl.short > 0;

  const process = async () => {
    if (!ready) return;
    setConfirming(true);
    try {
      await sourceApi.confirmScrape({ source_id: ready.source_id });
      watchKb(id, [ready.source_id]);
      qc.invalidateQueries({ queryKey: kbKeys.overview(id) });
      qc.invalidateQueries({ queryKey: kbKeys.funds });
      navigate(`/assistants/${id}/knowledge/site/${ready.source_id}`, { replace: true });
    } catch (e) {
      const st = (e as { response?: { status?: number } }).response?.status;
      if (st === 410) {
        message.info('Цена устарела — проверяем сайт заново');
        check();
      } else message.error(extractErrorMessage(e));
    } finally {
      setConfirming(false);
    }
  };

  // «Пополнить баланс»: сайт ждёт оплаты и запустится сам сразу после пополнения
  // (цена и список страниц сохраняются на 7 дней).
  const topup = async () => {
    if (!ready) return;
    try {
      await sourceApi.confirmScrape({ source_id: ready.source_id });
      // Деньги уже пришли (например, пополнили в другой вкладке) — сайт запущен.
      watchKb(id, [ready.source_id]);
      qc.invalidateQueries({ queryKey: kbKeys.overview(id) });
      navigate(`/assistants/${id}/knowledge/site/${ready.source_id}`, { replace: true });
      return;
    } catch (e) {
      const st = (e as { response?: { status?: number } }).response?.status;
      if (st !== 402) {
        message.error(extractErrorMessage(e));
        return;
      }
    }
    qc.invalidateQueries({ queryKey: kbKeys.overview(id) });
    navigate('/billing?topup=1');
  };

  const reason = failed ? (REASONS[failed.error_reason] ?? REASONS.internal_error) : null;

  return (
    <>
      <header className="v2-topbar is-section">
        <Link to={`/assistants/${id}/knowledge`} className="mp-back">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          База знаний
        </Link>
        <span style={{ color: '#D1D5DB' }}>/</span>
        <h1>Подключить сайт</h1>
      </header>
      <div className="v2-content is-section">
        <div className="kb-grid">
          <div className="site-main">
            <section className="v2-card site-card" aria-labelledby="s-url">
              <h2 id="s-url" className="v2-h2">
                <span className="v2-muted">1.</span> Адрес сайта
              </h2>
              <div className="site-url-row">
                <label htmlFor="site-url" className="v2-sr">
                  Адрес сайта
                </label>
                <input
                  id="site-url"
                  className="st-input"
                  style={{ height: 42, borderRadius: 10 }}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && check()}
                  placeholder="https://ваш-сайт.ru"
                />
                <button
                  type="button"
                  className="v2-btn is-primary"
                  style={{ height: 42 }}
                  onClick={check}
                  disabled={starting || scanning}
                >
                  {starting || scanning ? 'Проверяем…' : 'Проверить сайт'}
                </button>
              </div>
            </section>

            <section className="v2-card site-card" aria-labelledby="s-price">
              <h2 id="s-price" className="v2-h2">
                <span className="v2-muted">2.</span> Проверка и цена
              </h2>
              {!sourceId && (
                <div className="v2-small v2-muted">
                  Впишите адрес и нажмите «Проверить сайт» — покажем, сколько страниц обработаем и
                  сколько это будет стоить.
                </div>
              )}
              {scanning && (
                <div className="site-scan">
                  <span className="kb-spinner" aria-hidden="true" />
                  <div>
                    <div style={{ fontWeight: 600 }}>Читаем карту сайта…</div>
                    <div className="v2-xs v2-muted">Обычно 1–3 минуты, страницу можно закрыть.</div>
                  </div>
                </div>
              )}
              {sourceId && est === null && (
                <div className="v2-small v2-muted">
                  Проверка устарела — нажмите «Проверить сайт» ещё раз.
                </div>
              )}
              {failed && reason && (
                <div className="site-fail" role="alert">
                  <div style={{ fontWeight: 700 }}>{reason.title}</div>
                  <div className="v2-small">{reason.what}</div>
                  <div className="v2-xs v2-muted">Проверка бесплатная, деньги не списаны.</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <Link to={`/assistants/${id}/knowledge?upload=1`} className="v2-btn">
                      Загрузить страницы файлами
                    </Link>
                    <button type="button" className="v2-btn" onClick={check}>
                      Проверить снова
                    </button>
                  </div>
                </div>
              )}
              {ready && (
                <>
                  {ready.too_large && (
                    <div className="site-note">
                      <b>
                        На сайте {num(ready.total_pages_in_sitemap)} страниц — обработаем первые
                        200.
                      </b>{' '}
                      <span style={{ color: '#374151' }}>
                        Нужен весь сайт —{' '}
                        <a href={`mailto:${ready.contacts?.email ?? 'support@richat.ru'}`}>
                          напишите нам
                        </a>
                        , подключим отдельно.
                      </span>
                    </div>
                  )}
                  <div className="site-head">
                    <span
                      className="kb-type"
                      style={{ background: '#EFF6FF', color: '#1D4FA8', width: 36, height: 36 }}
                    >
                      <IconGlobe size={17} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div className="site-ok">
                        <span className="kb-toast-ok" style={{ width: 16, height: 16 }}>
                          <IconCheck size={9} />
                        </span>
                        Сайт проверен
                      </div>
                      <div style={{ fontWeight: 700 }}>{ready.site_title || ready.url}</div>
                      <div className="v2-xs v2-muted">{ready.url}</div>
                    </div>
                  </div>
                  <div className="site-stats">
                    <div>
                      <div className="v2-xs v2-muted">В карте сайта</div>
                      <b>{num(ready.total_pages_in_sitemap)} стр.</b>
                    </div>
                    <div>
                      <div className="v2-xs v2-muted">Обработаем</div>
                      <b>{ready.pages_to_process} стр.</b>
                    </div>
                  </div>
                  <div
                    className="up-sum-row"
                    style={{ fontSize: 13, borderTop: '1px solid #F3F4F6', paddingTop: 10 }}
                  >
                    <span>Страницы · {pages} × 0,5 ₽</span>
                    <span>{money(total)}</span>
                  </div>
                  <div className="up-sum-total" style={{ marginTop: 0 }}>
                    <span>Спишем не больше</span>
                    <span>{money(total)}</span>
                  </div>
                  <div className="v2-xs v2-muted">
                    Картинки с сайта не обрабатываем — берём только текст страниц. Резервируем при
                    запуске; страницы, которые не открылись, не оплачиваются, если не получится —
                    вернём всё.
                  </div>
                  {!short && fl.text && <div className="v2-xs v2-muted">{fl.text}</div>}
                  {short && (
                    <div role="alert" className="up-short">
                      <div style={{ flex: 1, fontSize: 13 }}>
                        <b>Не хватает {money(fl.short)}.</b>{' '}
                        <span style={{ color: '#374151' }}>
                          На балансе {money(funds?.available_rub ?? funds?.balance_rub)}. Пополните
                          баланс — обработка сайта начнётся сразу после оплаты.
                        </span>
                      </div>
                      <button type="button" className="v2-btn is-primary" onClick={topup}>
                        Пополнить баланс
                      </button>
                    </div>
                  )}
                  <div className="site-foot">
                    <span className="v2-xs v2-muted">
                      Цена действует 1 час, потом проверим сайт заново.
                    </span>
                    <button
                      type="button"
                      className="v2-btn is-primary"
                      style={{ height: 40 }}
                      disabled={short || confirming}
                      onClick={process}
                    >
                      Обработать сайт · {money(total, true)}
                    </button>
                  </div>
                </>
              )}
            </section>
          </div>
          <aside className="v2-card kb-aside" aria-labelledby="s-know">
            <h2 id="s-know" className="v2-h2">
              Что важно знать
            </h2>
            <ul className="site-list">
              <li>Берём страницы из карты сайта (sitemap.xml) — она есть у большинства сайтов.</li>
              <li>За один запуск — до 200 страниц.</li>
              <li>Проверка бесплатная и занимает 1–3 минуты. Платите только за обработку.</li>
              <li>
                Берём только текст страниц — картинки с сайта не обрабатываем. Нужны схемы или фото
                — загрузите их файлами.
              </li>
            </ul>
          </aside>
        </div>
      </div>
    </>
  );
}
